import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { enUS, es, pt } from 'date-fns/locale';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { default as dayPickerStyles } from 'react-day-picker/dist/style.module.css';
import { useSelector } from 'react-redux';
import ClickAwayListener from '/@/components/click-await-listener';
import Dialog from '/@/components/dialog';
import { isDateInRanges } from '/@/core/dates';
import { _ } from '/@/locale';
import { getSessionLanguage } from '/@/store/session/selectors';
import { getOS, isMobile } from '/@/utils';
import styles from './styles.module.css';

/** @param {(typeof DateInput)['propTypes']} props  */
export default function DateInput({
    color = 'gray-opacity',
    appearance = 'outline',
    size = 'md',
    app,
    bgColor,
    shadow,
    disabled,
    errorMessage,
    onChange,
    placeholder,
    validDates,
    value,
    showTooltip = false,
    tooltipMessage,
}) {
    const language = useSelector(getSessionLanguage);
    const [focused, setFocused] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(undefined);
    const [renderTooltip, setRenderTooltip] = useState(false);
    const [error, setError] = useState('');
    const mobile = isMobile();
    const handleDayPickerChange = (day, classes) => {
        if (classes[dayPickerStyles.disabled]) return;
        onChange(moment(day).format('YYYY-MM-DD'));
        setFocused(false);
    };
    const minValue = useMemo(() => {
        if (!validDates) return undefined;
        const startDates = validDates.map((dates) => dates[0]);
        if (startDates.includes(null)) return undefined;
        return startDates.sort()[0];
    }, [validDates]);
    const maxValue = useMemo(() => {
        if (!validDates) return undefined;
        const endDates = validDates.map((days) => days[1]);
        if (endDates.includes(null)) return undefined;
        return endDates.sort()[endDates.length - 1];
    }, [validDates]);

    useMemo(() => {
        const formattedCurrentMonth = moment(currentMonth).startOf('month');
        setRenderTooltip(formattedCurrentMonth.isAfter(maxValue));
    }, [currentMonth, maxValue]);

    const getRangeErrorText = (value) => {
        // OOR => Out of range.
        let OORText = '';
        if (minValue && value < minValue) {
            const min = moment(minValue).format('L');
            OORText = _('Por favor ingresá una fecha posterior al {{date}}.', { date: min });
        } else if (maxValue && value > maxValue) {
            const max = moment(maxValue).format('L');
            OORText = _('Por favor ingresá una fecha anterior al {{date}}.', { date: max });
        }

        return OORText;
    };

    const initialMonth = useMemo(() => {
        const min = minValue;
        const max = maxValue;

        if (value) return moment(value).toDate();
        if (!min || !max) return new Date();

        const now = moment();
        if (now.isBetween(min, max, 'days', '[]')) return now.toDate();
        return moment(min).toDate();
    }, [minValue, maxValue, value]);

    const handleInputBlur = () => {
        let errorText = '';
        if (getOS() === 'iOS' && value) {
            errorText = getRangeErrorText(value);
            if (errorText) {
                onChange(undefined);
            }
        }
        setFocused(false);
        setError(errorText);
    };

    const handleInputChange = (event) => {
        const { value } = event.target;

        if (getOS() === 'iOS' || isDateInRanges(moment(value), validDates)) {
            return onChange(value);
        }

        const message = getRangeErrorText(value) || errorMessage || _('Fecha inválida');

        alert(message);
    };

    const handleClickOnDisplay = () => {
        setFocused((prev) => !prev);
    };

    const displayValue = useMemo(() => value && moment(value).format('LL'), [value]);
    const getValueForDateInput = (value) => {
        return value ? moment(value).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    };

    return (
        <ClickAwayListener onClickAway={() => setFocused(false)}>
            <div>
                <div
                    className={classNames(
                        styles.root,
                        bgColor && styles[`border-color-${app}`],
                        styles[`color-${color}`],
                        styles[`appearance-${appearance}`],
                        app && styles.smaller,
                        disabled && styles.disabled,
                        shadow && styles.shadow,
                        focused && styles.focused
                    )}
                >
                    <Dialog
                        title={_('Error')}
                        message={error}
                        visible={Boolean(error)}
                        onClose={() => setError('')}
                        onAccept={() => setError('')}
                    />
                    <div
                        className={classNames(
                            styles.display,
                            disabled && styles.disabled,
                            bgColor && styles[`bg-color-${app}`]
                        )}
                        onClick={!mobile ? handleClickOnDisplay : undefined}
                    >
                        <div className={styles[`size-${size}`]}>
                            {value ? (
                                <span className={styles.value}>{displayValue}</span>
                            ) : (
                                <span className={styles.placeholder}>{placeholder}</span>
                            )}
                            <Tooltip
                                placement='right-end'
                                title={tooltipMessage}
                                open={focused && renderTooltip && showTooltip}
                                arrow
                                disableInteractive
                                style={{
                                    pointerEvents: 'none',
                                }}
                            >
                                <i className='material-icons'>date_range</i>
                            </Tooltip>
                        </div>
                    </div>
                    {mobile && (
                        <input
                            type='date'
                            disabled={disabled}
                            className={styles.input}
                            value={getValueForDateInput(value)}
                            min={minValue ? getValueForDateInput(minValue) : undefined}
                            max={maxValue ? getValueForDateInput(maxValue) : undefined}
                            onChange={handleInputChange}
                            onFocus={() => setFocused(true)}
                            onBlur={handleInputBlur}
                        />
                    )}
                    {!mobile && focused && !disabled && (
                        <DayPicker
                            mode='single'
                            classNames={{
                                ...dayPickerStyles,
                                day_selected: classNames(dayPickerStyles.day_selected, styles[`selected-day-${app}`]),
                                day: classNames(dayPickerStyles.day, styles[`day-${app}`]),
                                nav_button: classNames(dayPickerStyles.nav_button, styles[`day-${app}`]),
                            }}
                            onMonthChange={(month) => setCurrentMonth(month)}
                            defaultMonth={initialMonth}
                            selected={value ? moment(value).toDate() : undefined}
                            onSelect={handleDayPickerChange}
                            disabled={(day) => disabled || !isDateInRanges(day, validDates)}
                            ISOWeek={true}
                            locale={
                                {
                                    es: es,
                                    pt: pt,
                                    us: enUS,
                                }[language]
                            }
                        />
                    )}
                </div>
            </div>
        </ClickAwayListener>
    );
}

DateInput.propTypes = {
    color: PropTypes.oneOf(['gray-opacity']),
    appearance: PropTypes.oneOf(['fill', 'outline']),
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    validDates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    errorMessage: PropTypes.string,
    shadow: PropTypes.bool,
    disabled: PropTypes.bool,
    app: PropTypes.any,
    bgColor: PropTypes.bool,
};
